import { useRef, useState } from "react";
import { Upload, X, Image as ImageIcon, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { useTranslation } from "@/hooks/useTranslation";
import { cn } from "@/lib/utils";
import { GalleryUploadError, galleryUploadErrorMessageKey } from "@/services/api/eventSettings";

// Kept in sync with server/app/Http/Controllers/EventGalleryController.php
// (MAX_FILE_SIZE_KB, ALLOWED_EXTENSIONS) — JUB-84. Every accepted format is
// transcoded to JPEG server-side (HEIC in particular can't be rendered by any
// browser), so the whitelist here only gates what's worth uploading at all.
const MAX_SIZE_BYTES = 3 * 1024 * 1024; // 3MB
const ALLOWED_MIME_TYPES = ["image/jpeg", "image/jpg", "image/png", "image/webp", "image/heic", "image/heif"];
const ALLOWED_EXTENSIONS = [".jpg", ".jpeg", ".png", ".webp", ".heic", ".heif"];
const ACCEPT_ATTR = [...ALLOWED_EXTENSIONS, ...ALLOWED_MIME_TYPES].join(",");

/**
 * iOS Safari and some Android browsers report an empty/generic `file.type` for
 * HEIC camera photos, so the MIME check alone isn't reliable — fall back to
 * the filename extension.
 */
function isAllowedImageFile(file: File): boolean {
  if (ALLOWED_MIME_TYPES.includes(file.type)) return true;
  const name = file.name.toLowerCase();
  return ALLOWED_EXTENSIONS.some((ext) => name.endsWith(ext));
}

interface ImageUploadPlaceholderProps {
  currentImageUrl?: string | null;
  /**
   * Called with the new URL after an upload, or null on "remove". May return a
   * promise: a rejected removal keeps the image and shows the error instead of
   * a success message (JUB-39 — the server keeps the image when its file cannot
   * be deleted).
   */
  onChange?: (url: string | null) => void | Promise<void>;
  /** Shown instead of the "coming soon" message when there is no onUpload. */
  unavailableHint?: string;
  /** Called with the selected File; should resolve with the uploaded image URL. */
  onUpload?: (file: File) => Promise<string>;
  label?: string;
  disabled?: boolean;
  className?: string;
}

export function ImageUploadPlaceholder({
  currentImageUrl,
  onChange,
  onUpload,
  label,
  disabled = false,
  className,
  unavailableHint,
}: ImageUploadPlaceholderProps) {
  const { t } = useTranslation();
  const [uploading, setUploading] = useState(false);
  // JUB-39 (R6): feedback is shown inline under the image, announced politely,
  // not as global toasts — on a phone the stacked toasts covered the dialog's
  // Save button and swallowed the tap.
  const [status, setStatus] = useState<{ kind: "success" | "error" | "info"; text: string } | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleUploadClick = () => {
    if (disabled) return;
    if (onUpload) {
      fileInputRef.current?.click();
    } else {
      setStatus({ kind: "info", text: unavailableHint ?? t("events.form.imageUploadComingSoon") });
    }
  };

  const handleFileSelected = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !onUpload) return;
    // Reset input so the same file can be re-selected after removal
    e.target.value = "";

    if (!isAllowedImageFile(file)) {
      setStatus({ kind: "error", text: t("events.form.galleryInvalidFormat") ?? "Only JPG, PNG, WEBP or HEIC allowed." });
      return;
    }
    if (file.size > MAX_SIZE_BYTES) {
      setStatus({ kind: "error", text: t("events.form.galleryMaxSize") ?? "Image must not exceed 3MB." });
      return;
    }

    setUploading(true);
    setStatus(null);
    try {
      const url = await onUpload(file);
      onChange?.(url);
      setStatus({ kind: "success", text: t("events.form.imageUploaded") ?? "Image uploaded." });
    } catch (err) {
      const message = err instanceof GalleryUploadError
        ? t(galleryUploadErrorMessageKey(err.code)) ?? err.message
        : err instanceof Error ? err.message : (t("events.form.imageUploadError") ?? "Upload failed.");
      setStatus({ kind: "error", text: message });
    } finally {
      setUploading(false);
    }
  };

  const handleRemove = async () => {
    if (disabled || !onChange) return;
    setUploading(true);
    setStatus(null);
    try {
      await onChange(null);
      setStatus({ kind: "success", text: t("events.form.imageRemoved") });
    } catch (err) {
      setStatus({
        kind: "error",
        text: err instanceof GalleryUploadError
          ? t(galleryUploadErrorMessageKey(err.code)) ?? err.message
          : t("events.form.imageUploadError") ?? "Failed.",
      });
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className={cn("space-y-2", className)} data-testid="hero-image-upload">
      {label && <Label>{label}</Label>}
      {/* Hidden file input */}
      {onUpload && (
        <input
          ref={fileInputRef}
          type="file"
          accept={ACCEPT_ATTR}
          className="hidden"
          onChange={handleFileSelected}
          data-testid="hero-upload-input"
        />
      )}
      <div
        className={cn(
          "relative border-2 border-dashed rounded-lg overflow-hidden transition-colors",
          currentImageUrl ? "border-border" : "border-muted-foreground/25",
          disabled && "opacity-50 cursor-not-allowed",
          !disabled && "hover:border-muted-foreground/50"
        )}
      >
        {currentImageUrl ? (
          <div className="relative group">
            <img
              src={currentImageUrl}
              alt={label || "Uploaded image"}
              className="w-full h-48 object-cover"
              data-testid="hero-current-image"
            />
            {!disabled && (
              // JUB-39 (R4): CSS-only reveal. With a mouse the actions appear on
              // hover; keyboard focus inside reveals them too (focus-within);
              // on touch / coarse-pointer devices, where hover does not exist,
              // they are always shown as a compact row over the image's bottom
              // edge instead of a dimming overlay.
              <div
                data-testid="image-actions"
                className={cn(
                  "absolute inset-0 bg-black/50 flex items-center justify-center gap-2 transition-opacity",
                  "opacity-0 group-hover:opacity-100 group-focus-within:opacity-100",
                  "[@media(hover:none)]:opacity-100 [@media(hover:none)]:bg-transparent [@media(hover:none)]:items-end [@media(hover:none)]:justify-end [@media(hover:none)]:p-2",
                  "[@media(pointer:coarse)]:opacity-100 [@media(pointer:coarse)]:bg-transparent [@media(pointer:coarse)]:items-end [@media(pointer:coarse)]:justify-end [@media(pointer:coarse)]:p-2"
                )}
              >
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  onClick={handleUploadClick}
                  disabled={uploading}
                  className="bg-white/90 hover:bg-white focus-visible:ring-2 focus-visible:ring-pink-500 focus-visible:ring-offset-2"
                  data-testid="hero-replace-button"
                  aria-label={label ? t("events.form.replaceImageOf", { label }) : t("events.form.replaceImage")}
                >
                  {uploading ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Upload className="h-4 w-4 mr-2" />
                  )}
                  {t("events.form.replaceImage")}
                </Button>
                {onChange && (
                  <Button
                    type="button"
                    variant="destructive"
                    size="sm"
                    onClick={handleRemove}
                    disabled={uploading}
                    className="bg-red-600/90 hover:bg-red-600 focus-visible:ring-2 focus-visible:ring-pink-500 focus-visible:ring-offset-2"
                    data-testid="hero-remove-button"
                    aria-label={label ? t("events.form.removeImageOf", { label }) : t("events.form.removeImage")}
                  >
                    <X className="h-4 w-4 mr-2" />
                    {t("events.form.removeImage")}
                  </Button>
                )}
              </div>
            )}
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center h-48 p-6 text-center">
            <ImageIcon className="h-12 w-12 text-muted-foreground mb-3" />
            <p className="text-sm text-muted-foreground mb-3">
              {t("events.form.noImageSelected")}
            </p>
            <Button
              type="button"
              variant="outline"
              onClick={handleUploadClick}
              disabled={disabled || uploading}
              className="mt-2"
              data-testid="hero-upload-button"
            >
              {uploading ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Upload className="h-4 w-4 mr-2" />
              )}
              {t("events.form.uploadImage")}
            </Button>
          </div>
        )}
      </div>
      <p
        role="status"
        aria-live="polite"
        data-testid="image-status"
        className={cn(
          "min-h-[1.25rem] text-sm",
          status?.kind === "error" ? "text-destructive" : "text-muted-foreground"
        )}
      >
        {status?.text ?? ""}
      </p>
    </div>
  );
}
